import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import Address from '../../../model/project/address';
import { LoadingService } from '../../../services/loading.service';
import { Subject } from 'rxjs';
import {
  DEBOUNCE_TIME,
  SHORTER_DEBOUNCE_TIME,
} from '../../../services/request.service';
import { SubscriptionBasedComponent } from '../../../subscription-helpers/subscription-based.component';
import * as roundTo from 'round-to';
import AddressHelper from '../../address-helper';
import { StringHelper } from '../../string-helper';
import { ProjectDisplayObservableService } from 'src/app/services/project-display-observable.service';
import { MapsAPILoader } from '@ng-maps/core';
import {
  GoogleGeoCode,
  MapsService,
} from '../../../services/google-maps/maps.service';
import ProjectMapHelper from '../../project-map-helper';
import { CreateBaseProjectUrlParamsService } from '../../../services/create-base-project-url-params.service';
import PlaceResult = google.maps.places.PlaceResult;
import DistanceMatrixService = google.maps.DistanceMatrixService;
import Autocomplete = google.maps.places.Autocomplete;

const INPRESS_ADDRESS_REQUEST_STRING =
  'Terbekehofdreef 8, Unit A10, 2610, Wilrijk';

const DEFAULT_ADDRESS = 'Terbekehofdreef 8, 2610 Wilrijk';

@Component({
  selector: 'map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
})
export class MapComponent
  extends SubscriptionBasedComponent
  implements OnInit, OnDestroy
{
  @Input()
  inputAddress: Address;
  @Input()
  showMap = true;
  @Input()
  showInputFields = true;
  @Input()
  client = false;
  @Input()
  fromOldSystem = false;
  @Input()
  installerView = false;
  @Input()
  disabled = false;
  @Output()
  outputAddress = new EventEmitter();
  @Output()
  outputDistance = new EventEmitter<number>();
  @Output()
  outputIsValidAddress = new EventEmitter<boolean>();

  public latitude: number;
  public longitude: number;
  public searchControl: UntypedFormControl;
  public zoom: number;
  public bus = '';
  public line1 = '';
  public number = '';
  private addressChanged: Subject<string> = new Subject<string>();
  private numberChanged: Subject<string> = new Subject<string>();
  private checkAddressFields: Subject<any> = new Subject<any>();
  public loaded = false;

  @ViewChild('search', { static: true })
  public searchElementRef: ElementRef;
  @ViewChild('numberField', { static: true })
  public numberField: any;

  autocomplete: any;

  DEFAULT_ADDRESS = DEFAULT_ADDRESS;

  constructor(
    private mapsAPILoader: MapsAPILoader,
    private ngZone: NgZone,
    public mapsService: MapsService,
    private loadingService: LoadingService,
    private projectDisplayObservableService: ProjectDisplayObservableService,
    private createBaseProjectUrlParamsService: CreateBaseProjectUrlParamsService
  ) {
    super();
  }

  ngOnInit() {
    //set google maps defaults
    this.zoom = 16;
    this.latitude = ProjectMapHelper.INPRESS_COORDINATES.lat;
    this.longitude = ProjectMapHelper.INPRESS_COORDINATES.lng;

    this.fillAddressFromParams();

    if (this.inputAddress != null) {
      this.initFillForm();
    } else {
      this.initNewForm();
    }
    if (this.searchControl != null && this.disabled) {
      this.searchControl.disable();
    }
    this.loaded = true;

    this.initAddSubscriptions();
    this.initMapsAutoComplete();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.addressChanged != null) {
      this.addressChanged.complete();
      this.addressChanged = null;
    }
    if (this.numberChanged != null) {
      this.numberChanged.complete();
      this.numberChanged = null;
    }
    if (this.checkAddressFields != null) {
      this.checkAddressFields.complete();
      this.checkAddressFields = null;
    }
  }

  private initFillForm(): void {
    if (this.inputAddress.line1 == null) {
      this.inputAddress.line1 = '';
    }
    let numberStrings = AddressHelper.splitIntoLineAndNumber(
      this.inputAddress.line1
    );
    this.line1 = numberStrings[0].trim();
    let toSplit =
      numberStrings.length > 1 ? numberStrings[1] : numberStrings[0];
    let busStrings = toSplit.split('bus:');
    this.number =
      numberStrings.length > 1
        ? StringHelper.isEmptyOrWhiteSpace(numberStrings[1]) ||
          !numberStrings[1].includes(', bus:')
          ? numberStrings[1]
          : numberStrings[1].substring(0, numberStrings[1].indexOf(', bus:'))
        : '';
    this.bus = busStrings.length > 1 ? busStrings[1].trim() : '';
    if (this.number === '' && this.bus !== '') {
      this.line1 = busStrings[0];
    }

    if (this.inputAddress.line1 != null) {
      this.searchControl = new UntypedFormControl(this.line1);
      this.getCoordinatesFromAddress();
      this.projectDisplayObservableService
        .getValidStreetSubscribable()
        .next(true);

      this.checkEmptyAddressFields();
    } else {
      this.searchControl = new UntypedFormControl();
      this.setCurrentPosition();
      this.projectDisplayObservableService
        .getValidStreetSubscribable()
        .next(false);
    }
    this.checkAddressFields.next();
  }

  private initNewForm(): void {
    this.searchControl = new UntypedFormControl();
    this.setCurrentPosition();
    this.inputAddress = new Address();
    this.inputAddress.country = 'België';
    this.line1 = '';
    this.bus = '';
    this.projectDisplayObservableService
      .getValidStreetSubscribable()
      .next(false);
    this.projectDisplayObservableService
      .getValidAddressSubscribable()
      .next(false);
  }

  private checkEmptyAddressFields(): void {
    if (
      StringHelper.isEmptyOrWhiteSpace(this.line1) ||
      StringHelper.isEmptyOrWhiteSpace(this.number) ||
      StringHelper.isEmptyOrWhiteSpace(this.inputAddress.postalCode) ||
      StringHelper.isEmptyOrWhiteSpace(this.inputAddress.city) ||
      StringHelper.isEmptyOrWhiteSpace(this.inputAddress.country)
    ) {
      this.outputIsValidAddress.emit(false);
      this.projectDisplayObservableService
        .getValidAddressSubscribable()
        .next(false);
    } else {
      this.outputIsValidAddress.emit(true);
      this.projectDisplayObservableService
        .getValidAddressSubscribable()
        .next(true);
    }
  }

  private initAddSubscriptions(): void {
    this.addSubscription(
      this.addressChanged,
      () => {
        this.outputAddress.emit(this.inputAddress);
      },
      (err) => {},
      DEBOUNCE_TIME
    );
    this.addSubscription(
      this.numberChanged,
      () => {
        if (
          !StringHelper.isEmptyOrWhiteSpace(
            this.searchElementRef.nativeElement.value
          ) &&
          this.searchElementRef.nativeElement.value == this.line1
        ) {
          this.getCoordinatesFromAddress();
        }
      },
      (err) => {},
      SHORTER_DEBOUNCE_TIME
    );
    this.addSubscription(
      this.checkAddressFields,
      () => {
        this.checkEmptyAddressFields();
      },
      (err) => {},
      SHORTER_DEBOUNCE_TIME
    );
  }

  private initMapsAutoComplete(): void {
    this.mapsAPILoader.load().then(() => {
      const autocomplete = new Autocomplete(
        this.searchElementRef.nativeElement,
        {
          types: ['address'],
        }
      );
      autocomplete.addListener('place_changed', () => {
        const place: PlaceResult = autocomplete.getPlace();
        if (!place) return;

        this.setPlaceResult(place);
      });
    });
  }

  private fillAddressFromParams() {
    const params =
      this.createBaseProjectUrlParamsService.getCreateBaseProjectUrlParams();
    if (!params.address) return;

    this.mapsAPILoader.load().then(() => {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: params.address }).then((geocoderResponse) => {
        const place: PlaceResult = geocoderResponse.results[0];
        if (!place) return;

        this.setPlaceResult(place);
      });
    });
  }

  private setPlaceResult(place: google.maps.places.PlaceResult) {
    this.ngZone.run(() => {
      //verify result
      if (place.geometry === undefined || place.geometry === null) {
        this.outputIsValidAddress.emit(false);
        this.projectDisplayObservableService
          .getValidStreetSubscribable()
          .next(false);
        return;
      }

      //set latitude, longitude and zoom
      this.latitude = place.geometry.location.lat();
      this.longitude = place.geometry.location.lng();
      this.setAddress(place);
      this.getDrivingDistance();
      this.outputIsValidAddress.emit(true);
      this.projectDisplayObservableService
        .getValidStreetSubscribable()
        .next(true);
    });
  }

  public lineCheck() {
    if (
      StringHelper.isEmptyOrWhiteSpace(
        this.searchElementRef.nativeElement.value
      ) ||
      this.searchElementRef.nativeElement.value !== this.line1
    ) {
      this.projectDisplayObservableService
        .getValidStreetSubscribable()
        .next(false);
      this.outputIsValidAddress.emit(false);
    }
  }

  private setCurrentPosition() {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition((position) => {
        this.latitude = position.coords.latitude;
        this.longitude = position.coords.longitude;
      });
    }
  }

  parseAddressFieldChange(value) {
    this.outputAddress.emit(this.inputAddress);
    if (StringHelper.isEmptyOrWhiteSpace(value)) {
      this.projectDisplayObservableService
        .getValidAddressSubscribable()
        .next(false);
    } else {
      this.checkAddressFields.next();
    }
  }

  setAddress(place: PlaceResult) {
    const googleFields = this.getGoogleFields(place);
    this.inputAddress.postalCode = googleFields.postal_code;
    this.inputAddress.city = googleFields.locality;
    this.line1 = googleFields.route;

    const number = googleFields.street_number;
    this.number = number == null || number === '' ? this.number : number;

    const bus = googleFields.subpremise;
    const busStrings = bus.split('bus ');
    this.bus = busStrings.length > 1 ? busStrings[1].trim() : '';

    this.line1Change();
    this.inputAddress.country = googleFields.country;
    this.searchControl.setValue(this.line1);
    this.numberField.nativeElement.focus();
    this.numberField.nativeElement.select();
    if (this.inputAddress.coordinates != null) {
      this.inputAddress.coordinates.lat = this.latitude;
      this.inputAddress.coordinates.lng = this.longitude;
    }
    this.outputAddress.emit(this.inputAddress);
    this.checkAddressFields.next();
  }

  getNewResult = () => {
    return {
      postal_code: '',
      locality: '',
      country: '',
      route: '',
      street_number: '',
      subpremise: '',
    };
  };

  getGoogleFields(place: PlaceResult) {
    const result = this.getNewResult();
    const keys = Object.keys(result);
    place.address_components.forEach((component) => {
      const types = component.types;
      keys.forEach((key) => {
        if (types.includes(key)) {
          result[key] += component.long_name;
        }
      });
    });
    return result;
  }

  getCoordinatesFromAddress() {
    let address = this.constructRequestAddress();
    let addressWasEmpty = false;

    if (address == null || address === '') {
      address = DEFAULT_ADDRESS;
      addressWasEmpty = true;
    }

    if (address != null || address != '') {
      this.addSubscription(
        this.mapsService.getGeocode(address),
        (googleGeoCode: GoogleGeoCode) => {
          if (googleGeoCode != null) {
            this.latitude = googleGeoCode.coordinates.lat;
            this.longitude = googleGeoCode.coordinates.lng;
            if (!addressWasEmpty && this.inputAddress.coordinates != null) {
              this.inputAddress.coordinates.lat = this.latitude;
              this.inputAddress.coordinates.lng = this.longitude;
            }
            if (
              !addressWasEmpty &&
              StringHelper.isEmpty(this.inputAddress.postalCode)
            ) {
              this.attemptGetPostalCode(googleGeoCode.postalCode);
            }
            this.outputIsValidAddress.emit(!addressWasEmpty);
          } else {
            this.latitude = ProjectMapHelper.INPRESS_COORDINATES.lat;
            this.longitude = ProjectMapHelper.INPRESS_COORDINATES.lng;
            this.outputIsValidAddress.emit(false);
          }
        }
      );
    }
  }

  attemptGetPostalCode(postalCode: string) {
    this.inputAddress.postalCode = postalCode;
    this.outputAddress.emit(this.inputAddress);
    this.getDrivingDistance();
    this.checkAddressFields.next();
  }

  getDrivingDistance() {
    let address = this.constructRequestAddress();
    if (address == null || address === '') {
      address = DEFAULT_ADDRESS;
    }
    new DistanceMatrixService().getDistanceMatrix(
      {
        origins: [INPRESS_ADDRESS_REQUEST_STRING],
        destinations: [address],
        travelMode: google.maps.TravelMode.DRIVING,
      },
      (results: any) => {
        if (
          results != null &&
          results.rows[0] != null &&
          results.rows[0].elements[0] != null
        ) {
          this.outputDistance.emit(
            roundTo(results.rows[0].elements[0].distance.value * 0.001, 2)
          );
        }
      }
    );
  }

  constructRequestAddress() {
    return [
      this.line1,
      this.number,
      this.inputAddress.postalCode,
      this.inputAddress.city,
      this.inputAddress.country,
    ]
      .filter((s) => s != null && s !== '')
      .join(', ');
  }

  numberChange() {
    this.numberChanged.next();
    if (StringHelper.isEmptyOrWhiteSpace(this.number)) {
      this.projectDisplayObservableService
        .getValidAddressSubscribable()
        .next(false);
    } else {
      this.checkAddressFields.next();
    }
    this.line1Change();
  }

  line1Change() {
    let outputLine1 = this.line1.trim();
    let outputBus = this.bus.trim();
    let outputNumber = this.number.trim();

    if (
      outputBus
        .substring(0, 3)
        .localeCompare('bus:', undefined, { sensitivity: 'base' }) === 0
    ) {
      outputBus = outputBus.substring(3);
    }
    this.inputAddress.line1 =
      (outputLine1.replace(/ /g, '') != '' ? outputLine1 + ' ' : '') +
      (outputNumber.replace(/ /g, '') != '' ? outputNumber + ' ' : '');
    if (this.bus === '' && this.inputAddress.line1.length > 0) {
      this.inputAddress.line1 = this.inputAddress.line1.substring(
        0,
        this.inputAddress.line1.length - 1
      );
    } else {
      this.inputAddress.line1 =
        this.inputAddress.line1 === ''
          ? 'bus: ' + this.bus
          : this.inputAddress.line1.trim() + ', bus: ' + this.bus;
    }
    if (!this.client && !this.fromOldSystem && !this.installerView) {
      this.loadingService.isLoading();
    }
    this.addressChanged.next();
  }
}
